import React from 'react';

import { Navigate, Route, Routes } from 'react-router-dom';

import { PERMISSION } from 'src/features/auth/types';
import useHasPermissions from 'src/hooks/useHasPermissions';
import AdministrationLayout from 'src/features/routes/administration/AdministrationLayout';
import { useSidePanel } from 'src/registry/providers/SidePanelProvider';
import Sites from 'src/features/sites/Sites';
import SiteDashboard from 'src/features/sites/components/SiteDashboard';
import ServiceProviders from 'src/features/serviceProvider/routes/ServiceProviders';
import Zones from 'src/features/zone/routes/Zones';
import ZoneDashboard from 'src/features/zone/routes/ZoneDashboard';
import GatewaysTable from 'src/features/gateway/components/GatewaysTable';
import GatewayDashboard from 'src/features/gateway/components/GatewayDashboard';
import Spaces from 'src/features/spaces/routes/Spaces';
import AccessKeyTable from 'src/features/accessKey/routes/AccessKeyTable';
import Actions from 'src/features/actions/routes/Actions';
import SystemComponents from 'src/features/systems/routes/SystemComponents';
import PricingPlans from 'src/features/pricingPlan/routes/PricingPlans';
import PricingPlanDashboard from 'src/features/pricingPlan/routes/PricingPlanDashboard';
import PricingPlanVersions from 'src/features/pricingPlan/routes/PricingPlanVersions';
import MembershipTypesTable from 'src/features/membershipType/components/MembershipTypesTable';
import MembershipTypeDashboard from 'src/features/membershipType/components/MembershipTypeDashboard';
import BillingEntitiesTable from 'src/features/billingEntity/components/tables/BillingEntitiesTable';
import ValidationTypesLayout from 'src/features/validationTypes/components/ValidationTypesLayout';
import ValidatorAccountsTable from 'src/features/validatorAccounts/components/tables/ValidatorAccountsTable';
import ValidatorAccountDashboard from 'src/features/validatorAccounts/components/ValidatorAccountDashboard';
import SystemSettingsLayout from 'src/features/systems/routes/SystemSettingsLayout';
import UsersLayout from 'src/features/users/routes/UsersLayout';
import RolesTable from 'src/features/roles/components/tables/RolesTable';
import RoleDashboard from 'src/features/roles/components/RoleDashboard';
import DisplaySettingsFormModel from 'src/features/systems/routes/DisplaySettingsFormModel';
import ApiKeys from 'src/features/apiKeys/components/tables/ApiKeys';
import WhiteLists from 'src/features/allowList/WhiteLists';
import AllowListDashboard from 'src/features/allowList/AllowListDashboard';
import BlackLists from 'src/features/allowList/BlackLists';

const AdministrationRoutes = () => {
    const accessServiceProviders = useHasPermissions([PERMISSION.ACCESS_SERVICE_PROVIDERS]);
    const accessSites = useHasPermissions([PERMISSION.ACCESS_SITES]);
    const accessZones = useHasPermissions([PERMISSION.ACCESS_ZONES]);
    const accessGateways = useHasPermissions([PERMISSION.ACCESS_GATEWAYS]);
    const accessSpaces = useHasPermissions([PERMISSION.ACCESS_SPACES]);
    const accessRoles = useHasPermissions([PERMISSION.ACCESS_ROLES]);
    const accessUsers = useHasPermissions([PERMISSION.ACCESS_USERS]);
    const accessPricingPlans = useHasPermissions([PERMISSION.ACCESS_PRICING_PLANS]);
    const manageSystemSettings = useHasPermissions([PERMISSION.MANAGE_SYSTEM_SETTINGS]);
    const accessMembershipTypes = useHasPermissions([PERMISSION.ACCESS_MEMBERSHIP_TYPES]);
    const manageSystemComponents = useHasPermissions([PERMISSION.MANAGE_SYSTEM_COMPONENTS]);
    const accessAccessKeys = useHasPermissions([PERMISSION.ACCESS_ACCESS_KEYS]);
    const accessTroubleshooting = useHasPermissions([PERMISSION.ACCESS_TROUBLESHOOTING]);
    const accessValidatorAccounts = useHasPermissions([PERMISSION.MANAGE_VALIDATIONS]);
    const manageApiKeys = useHasPermissions([PERMISSION.MANAGE_API_KEYS]);
    const accessAllowLists = useHasPermissions([PERMISSION.ACCESS_ALLOW_LISTS]);

    const { setForceLeftStaticPanel } = useSidePanel();

    React.useEffect(() => {
        setForceLeftStaticPanel(true);

        return () => setForceLeftStaticPanel(false);
    }, [setForceLeftStaticPanel]);

    const defaultRoutePath = (() => {
        if (accessUsers) return 'users';
        if (accessRoles) return 'roles';
        if (accessServiceProviders) return 'service-provider';
        if (accessSites) return 'site';
        if (accessZones) return 'zone';
        if (accessGateways) return 'gateway';
        if (accessSpaces) return 'spaces';
        if (accessMembershipTypes) return 'membership-type';
        if (accessAccessKeys) return 'access-key';
        if (accessValidatorAccounts) return 'validation-types';
        if (accessPricingPlans) return 'pricing-plan';
        if (accessServiceProviders) return 'billing-entity';
        if (accessTroubleshooting) return 'actions';
        if (manageSystemSettings) return 'system-settings';
        if (manageSystemComponents) return 'system-components';
        if (manageApiKeys) return 'api-keys';

        return 'no-access';
    })();

    return (
        <Routes>
            <Route path="" element={<AdministrationLayout />}>
                <Route index element={<Navigate to={defaultRoutePath} replace />} />
                {accessUsers && <Route path="users" element={<UsersLayout />} />}
                {accessRoles && (
                    <>
                        <Route path="roles" element={<RolesTable />} />
                        <Route path="roles/:roleId" element={<RoleDashboard />} />
                    </>
                )}

                {accessServiceProviders && <Route path="service-provider" element={<ServiceProviders />} />}
                {accessSites && (
                    <>
                        <Route path="site" element={<Sites />} />
                        <Route path="site/:siteId" element={<SiteDashboard />} />
                    </>
                )}
                {accessZones && (
                    <>
                        <Route path="zone" element={<Zones />} />
                        <Route path="zone/:zoneId" element={<ZoneDashboard />} />
                    </>
                )}
                {accessGateways && (
                    <>
                        <Route path="gateway" element={<GatewaysTable />} />
                        <Route path="gateway/:gatewayId" element={<GatewayDashboard />} />
                    </>
                )}
                {accessSpaces && <Route path="spaces" element={<Spaces />} />}

                {accessAccessKeys && <Route path="access-key" element={<AccessKeyTable />} />}
                {accessTroubleshooting && <Route path="actions" element={<Actions />} />}

                {accessValidatorAccounts && (
                    <>
                        <Route path="validation-types" element={<ValidationTypesLayout />} />
                        <Route path="validator-accounts" element={<ValidatorAccountsTable />} />
                        <Route path="validator-accounts/:accountId" element={<ValidatorAccountDashboard />} />
                    </>
                )}

                {accessPricingPlans && (
                    <>
                        <Route path="pricing-plan" element={<PricingPlans />} />
                        <Route path="pricing-plan/:planId" element={<PricingPlanDashboard />} />
                        <Route path="pricing-plan/:planId/:identity" element={<PricingPlanVersions />} />
                    </>
                )}

                {accessServiceProviders && <Route path="billing-entity" element={<BillingEntitiesTable />} />}

                {manageSystemSettings && (
                    <>
                        <Route path="system-settings" element={<SystemSettingsLayout />} />
                        <Route path="display-settings/:displayModel" element={<DisplaySettingsFormModel />} />
                    </>
                )}
                {manageSystemComponents && <Route path="system-components" element={<SystemComponents />} />}
                {manageApiKeys && <Route path="api-keys" element={<ApiKeys />} />}
                {accessMembershipTypes && (
                    <>
                        <Route path="membership-templates" element={<MembershipTypesTable />} />
                        <Route path="membership-templates/:membershipTypeId" element={<MembershipTypeDashboard />} />
                    </>
                )}
                {accessAllowLists && (
                    <>
                        <Route path='white-list' element={<WhiteLists />} />
                        <Route path='white-list/:listId' element={<AllowListDashboard />} />
                        <Route path='black-list' element={<BlackLists />} />
                        <Route path='black-list/:listId' element={<AllowListDashboard />} />
                    </>
                )}
                <Route path="*" element={<Navigate to=".." />} />
            </Route>
        </Routes>
    );
};

export default AdministrationRoutes;
